import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import Header from '../../components/customer/Header';
import styles from './Cart.module.css'

const Cart = () => {
    const { 
        cartItems, 
        updateQuantity, 
        removeFromCart, 
        getCartTotal, 
        clearCart, 
        loading 
    } = useCart();

    const navigate = useNavigate();

    const handleUpdateQuantity = async (productId, currentQuantity, delta) => {
        const newQuantity = currentQuantity + delta;
        if (newQuantity > 0) {
            await updateQuantity(productId, newQuantity);
        }
    };

    const handleRemoveItem = async (productId) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este producto?')) {
            await removeFromCart(productId);
        }
    };

    // Función segura para formatear precios
    const formatPrice = (price) => {
        if (!price) return '$0.00';
        try {
            return typeof price === 'number' ? `$${price.toFixed(2)}` : price;
        } catch (error) {
            return '$0.00';
        }
    };

    // Función segura para calcular total por item
    const getItemTotal = (item) => {
        if (!item || !item.price) return 0;
        try {
            const priceString = item.price.toString().replace('$', '').replace(',', '');
            const price = parseFloat(priceString);
            return isNaN(price) ? 0 : price * (item.quantity || 1);
        } catch (error) {
            return 0;
        }
    };

    if (loading) {
        return (
            <div className={styles.cartPage}>
                <Header />
                <div className={styles.loading}>Cargando carrito...</div>
            </div>
        );
    }

    if (!cartItems?.length) {
        return (
            <div className={styles.cartPage}>
                <Header />
                <div className={styles.emptyCart}>
                    <div className={styles.emptyContent}>
                        <h2>Tu carrito está vacío</h2>
                        <p>¿No sabes qué comprar? ¡Miles de productos te esperan!</p>
                        <Link to="/" className={styles.continueShopping}>
                            Continuar comprando
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const cartTotal = getCartTotal();

    const handleClearCart = async () => {
      const guestEmail = localStorage.getItem('guest_email');
      if (!guestEmail) return;

      if (window.confirm('¿Estás seguro de que quieres vaciar el carrito?')) {
        await clearCart(guestEmail);
      }
    };

    return(
      <div className={styles.cartPage}>
        <Header />
        
        <div className={styles.cartContainer}>
          <div className={styles.cartHeader}>
            <h1>Tu Carrito de Compras</h1>
            <button 
              onClick={handleClearCart} 
              className={styles.clearCartBtn}
              disabled={cartItems.length === 0}
            >
              Vaciar carrito
            </button>
          </div>

          <div className={styles.cartContent}>
            <div className={styles.cartItems}>
              {cartItems.map((item) => (
                <div key={item.id} className={styles.cartItem}>
                  <img 
                    src={item.primary_image || '/placeholder-image.jpg'} 
                    alt={item.name}
                    className={styles.itemImage}
                  />
                  
                  <div className={styles.itemDetails}>
                    <h3 className={styles.itemName}>{item.name}</h3>
                    <p className={styles.itemPrice}>
                      {formatPrice(parseFloat(item.price))}
                    </p>
                    {item.is_prime && (
                      <span className={styles.primeBadge}>Prime ✓</span>
                    )}
                  </div>

                  <div className={styles.quantityControls}>
                    <button
                      onClick={() => handleUpdateQuantity(item.id, item.quantity, -1)}
                      className={styles.quantityBtn}
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <span className={styles.quantity}>{item.quantity}</span>
                    <button
                      onClick={() => handleUpdateQuantity(item.id, item.quantity, 1)}
                      className={styles.quantityBtn}
                      disabled={item.quantity >= item.stock_quantity}
                    >
                      +
                    </button>
                  </div>

                  <div className={styles.itemTotal}>
                    {formatPrice(getItemTotal(item))}
                  </div>

                  <button
                    onClick={() => handleRemoveItem(item.id)}
                    className={styles.removeBtn}
                    aria-label="Eliminar producto"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>

            <div className={styles.cartSummary}>
              <div className={styles.summaryCard}>
                <h3>Resumen del pedido</h3>
                
                <div className={styles.summaryRow}>
                   <span>Subtotal ({cartItems.reduce((total, item) => total + (item.quantity || 1), 0)} items):</span>
                  <span>{formatPrice(cartTotal)}</span> {/* ✅ Usar función segura */}
                </div>

                <div className={styles.summaryRow}>
                  <span>Envío:</span>
                  <span className={styles.freeShipping}>GRATIS</span>
                </div>

                <div className={styles.summaryDivider}></div>

                <div className={styles.summaryRow}>
                  <strong>Total:</strong>
                   <strong className={styles.totalPrice}>{formatPrice(cartTotal)}</strong> {/* ✅ Usar función segura */}
                </div>

                <button
                  className={styles.checkoutBtn}
                  onClick={() => navigate('/Checkout')}
                >
                  Proceder al pago
                </button>

                <Link to="/" className={styles.continueLink}>
                  ← Continuar comprando
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
};

export default Cart;